import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFieldSingleSelectOption} from '@github-ui/issue-fields-shared/issue-field'
import {IssueSingleSelectFieldPicker} from '@github-ui/item-picker/IssueSingleSelectFieldPicker'
import {Button} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import {LABELS} from './constants/labels'
import fieldItemStyles from './IssueFieldItem.module.css'
import {IssueFieldSingleSelectValueToken} from './IssueFieldSingleSelectValueToken'
import styles from './IssueFieldSingleSelectValueToken.module.css'

type IssueFieldCommentEditorProps = {fieldId: string; fieldName: string; readonly: boolean}

export type SingleSelectInitialValue = {
  name: string
  color: string
  description?: string
} | null
export type SingleSelectOnCommit = (fieldId: string, value: IssueFieldSingleSelectOption | null) => void

type IssueFieldSingleSelectEditorBaseProps = {
  fieldId: string
  fieldName: string
  initialValue: SingleSelectInitialValue
  onCommit: SingleSelectOnCommit
  shouldOpen?: boolean
  isDraft?: boolean
}

export type IssueFieldSingleSelectEditorProps = IssueFieldCommentEditorProps & {
  initialValue: SingleSelectInitialValue
  onCommit: SingleSelectOnCommit
  /// If set to true, then the single select editor should render in open state
  shouldOpen?: boolean
  isDraft?: boolean
  className?: string
}

export type IssueFieldCompactSingleSelectEditorInternalProps = IssueFieldSingleSelectEditorBaseProps & {
  readonly: boolean
}

export const IssueFieldCompactSingleSelectEditorInternal = ({
  fieldId,
  fieldName,
  initialValue,
  onCommit,
  shouldOpen = false,
  isDraft = false,
  readonly,
}: IssueFieldCompactSingleSelectEditorInternalProps) => {
  const onSelectionChanged = useCallback(
    (selectedOption: IssueFieldSingleSelectOption | null) => {
      onCommit(fieldId, selectedOption)
    },
    [fieldId, onCommit],
  )

  const onClose = useCallback(() => {
    if (isDraft) {
      onCommit(fieldId, null)
    }
  }, [fieldId, isDraft, onCommit])

  const isNoValue = !initialValue || (initialValue.name === '' && initialValue.color === '')

  const displayValue = isNoValue ? (
    LABELS.setValue
  ) : (
    <IssueFieldSingleSelectValueToken
      name={initialValue.name}
      color={initialValue.color}
      getTooltipText={() => undefined}
      size="small"
      containerClassName={styles.compactContainer}
    />
  )

  const ariaLabel = isNoValue ? `Set ${fieldName}` : `Edit ${fieldName}`

  return (
    <div className={fieldItemStyles.issueFieldFormWrapper}>
      <p className={fieldItemStyles.issueFieldLabel}>{fieldName}</p>
      {readonly ? (
        <div className={fieldItemStyles.issueFieldValue}>{displayValue}</div>
      ) : (
        <IssueSingleSelectFieldPicker
          fieldId={fieldId}
          fieldName={fieldName}
          selectedOption={initialValue ? initialValue.name : null}
          onSelectionChange={onSelectionChanged}
          anchorElement={(anchorProps, ref) => (
            <Button
              ref={ref}
              variant="invisible"
              size="small"
              alignContent="start"
              className={fieldItemStyles.issueFieldValue}
              aria-label={ariaLabel}
              {...anchorProps}
            >
              {displayValue}
            </Button>
          )}
          readonly={false}
          isLazy
          onClose={onClose}
          shouldOpen={shouldOpen}
        />
      )}
    </div>
  )
}

export const IssueFieldSingleSelectEditor = ({
  fieldId,
  fieldName,
  readonly,
  initialValue,
  onCommit,
  shouldOpen = false,
  isDraft = false,
  className,
}: IssueFieldSingleSelectEditorProps) => {
  const isCompactView = isFeatureEnabled('issue_fields_compact_view')

  const onSelectionChanged = useCallback(
    (selectedOption: IssueFieldSingleSelectOption | null) => {
      onCommit(fieldId, selectedOption)
    },
    [fieldId, onCommit],
  )

  const onClose = useCallback(() => {
    if (isDraft) {
      onCommit(fieldId, null)
    }
  }, [fieldId, isDraft, onCommit])

  const sectionHeader = useMemo(() => {
    return (
      <IssueSingleSelectFieldPicker
        fieldId={fieldId}
        fieldName={fieldName}
        selectedOption={initialValue ? initialValue.name : null}
        onSelectionChange={onSelectionChanged}
        anchorElement={(anchorProps, ref) => (
          <SectionHeader title={fieldName || ''} buttonProps={anchorProps} ref={ref} readonly={readonly} />
        )}
        readonly={false}
        isLazy
        onClose={onClose}
        shouldOpen={shouldOpen}
      />
    )
  }, [fieldId, fieldName, initialValue, onClose, onSelectionChanged, readonly, shouldOpen])

  if (isCompactView) {
    return (
      <IssueFieldCompactSingleSelectEditorInternal
        fieldId={fieldId}
        fieldName={fieldName}
        initialValue={initialValue}
        onCommit={onCommit}
        shouldOpen={shouldOpen}
        isDraft={isDraft}
        readonly={readonly}
      />
    )
  }

  const isNoValue = initialValue?.name === '' && initialValue?.color === '' && initialValue?.description === ''
  return (
    <Section sectionHeader={sectionHeader} className={className}>
      <div className={styles.fieldEditorContainer}>
        {isNoValue ? (
          <div className={styles.noValueContainer}>{LABELS.setValue}</div>
        ) : (
          <IssueFieldSingleSelectValueToken
            name={initialValue?.name || ''}
            color={initialValue?.color || ''}
            getTooltipText={isTextTruncated => (isTextTruncated ? initialValue?.description ?? '' : undefined)}
          />
        )}
      </div>
    </Section>
  )
}

try{ IssueFieldCompactSingleSelectEditorInternal.displayName ||= 'IssueFieldCompactSingleSelectEditorInternal' } catch {}
try{ IssueFieldSingleSelectEditor.displayName ||= 'IssueFieldSingleSelectEditor' } catch {}